import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format, parseISO } from 'date-fns';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Download, Mail, Calendar, Loader2, RefreshCw,
  Phone, MessageSquare, ChevronDown, ChevronRight, Users,
  FileSpreadsheet, Clock, Trash2, Plus, CheckSquare, Square,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

const PERIODS = [
  { value: 'this_week',  label: 'This Week' },
  { value: 'last_week',  label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'ytd',        label: 'Year to Date' },
];

const TYPE_ICONS = {
  call:  <Phone className="h-3.5 w-3.5 text-blue-500" />,
  email: <Mail className="h-3.5 w-3.5 text-purple-500" />,
  sms:   <MessageSquare className="h-3.5 w-3.5 text-green-500" />,
};

const TYPE_COLORS = {
  call:  'bg-blue-50 text-blue-700',
  email: 'bg-purple-50 text-purple-700',
  sms:   'bg-green-50 text-green-700',
};

// ── User multi-select ───────────────────────────────────────────────────────
function UserSelector({ users, selected, onChange }) {
  const allSelected = selected.length === 0; // empty = "all"
  return (
    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
      <button
        onClick={() => onChange([])}
        className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${allSelected ? 'bg-indigo-600 text-white' : 'hover:bg-slate-100 text-slate-700'}`}
      >
        {allSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
        All Users
      </button>
      {users.map(u => {
        const checked = selected.includes(u.id);
        return (
          <button
            key={u.id}
            onClick={() => {
              if (checked) onChange(selected.filter(id => id !== u.id));
              else onChange([...selected, u.id]);
            }}
            className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${checked ? 'bg-indigo-50 text-indigo-700 font-medium' : 'hover:bg-slate-100 text-slate-700'}`}
          >
            {checked ? <CheckSquare className="h-3.5 w-3.5 text-indigo-600" /> : <Square className="h-3.5 w-3.5 text-slate-400" />}
            <span className="truncate">{u.name}</span>
            <span className="ml-auto text-xs text-slate-400">{u.count}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── Summary row (expandable) ────────────────────────────────────────────────
function SummaryUserRow({ userData }) {
  const [expanded, setExpanded] = useState(false);
  const { user_name, totals, by_date } = userData;
  const dates = Object.keys(by_date).sort();
  const total = totals.calls + totals.emails + totals.sms;

  return (
    <>
      <tr
        className="border-b hover:bg-slate-50 cursor-pointer"
        onClick={() => setExpanded(e => !e)}
      >
        <td className="px-4 py-3 font-medium text-slate-800 flex items-center gap-2">
          {expanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
          {user_name}
        </td>
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center gap-1 font-semibold text-blue-700">{totals.calls}</span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center gap-1 font-semibold text-purple-700">{totals.emails}</span>
        </td>
        <td className="px-4 py-3 text-center">
          <span className="inline-flex items-center gap-1 font-semibold text-green-700">{totals.sms}</span>
        </td>
        <td className="px-4 py-3 text-center font-bold text-slate-800">{total}</td>
      </tr>
      {expanded && dates.map(date => {
        const d = by_date[date];
        const dayTotal = d.calls + d.emails + d.sms;
        if (!dayTotal) return null;
        return (
          <tr key={date} className="bg-slate-50 border-b text-sm">
            <td className="px-4 py-2 pl-10 text-slate-500">
              {format(parseISO(date), 'EEE, MMM d')}
            </td>
            <td className="px-4 py-2 text-center text-blue-600">{d.calls || '—'}</td>
            <td className="px-4 py-2 text-center text-purple-600">{d.emails || '—'}</td>
            <td className="px-4 py-2 text-center text-green-600">{d.sms || '—'}</td>
            <td className="px-4 py-2 text-center text-slate-600">{dayTotal}</td>
          </tr>
        );
      })}
    </>
  );
}

// ── Email / Schedule dialogs ─────────────────────────────────────────────────
function EmailDialog({ open, onClose, periodLabel, tableHtml }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [msg, setMsg] = useState('');
  const [sending, setSending] = useState(false);

  const send = async () => {
    const toList = to.split(/[,\s]+/).map(e => e.trim()).filter(Boolean);
    if (!toList.length) { toast.error('Enter at least one recipient'); return; }
    setSending(true);
    try {
      await axios.post(`${API}/reports/activity/email`, {
        to_emails: toList,
        cc_emails: cc.split(/[,\s]+/).map(e => e.trim()).filter(Boolean),
        subject: `CRM Activity Report — ${periodLabel}`,
        message: msg,
        html_table: tableHtml,
        period_label: periodLabel,
      });
      toast.success('Report emailed successfully');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5 text-indigo-600" />Email Report</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>To <span className="text-red-500">*</span></Label>
            <Input value={to} onChange={e => setTo(e.target.value)} placeholder="email1@example.com, email2@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label>CC</Label>
            <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="Optional note to include in the email..." rows={3} />
          </div>
          <p className="text-xs text-slate-500">Subject: CRM Activity Report — {periodLabel}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending}>
            {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
            Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleDialog({ open, onClose, allUsers }) {
  const [freq, setFreq] = useState('weekly');
  const [dow, setDow] = useState(1); // Monday
  const [hour, setHour] = useState(8);
  const [period, setPeriod] = useState('this_week');
  const [view, setView] = useState('summary');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [msg, setMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    if (open) loadSchedules();
  }, [open]);

  const loadSchedules = async () => {
    try {
      const res = await axios.get(`${API}/reports/activity/scheduled`);
      setSchedules(res.data);
    } catch {}
  };

  const save = async () => {
    const toList = to.split(/[,\s]+/).map(e => e.trim()).filter(Boolean);
    if (!toList.length) { toast.error('Enter at least one recipient'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/reports/activity/scheduled`, {
        frequency: freq,
        day_of_week: freq === 'weekly' ? dow : null,
        hour,
        period,
        view_type: view,
        user_ids: selectedUsers.length ? selectedUsers.join(',') : 'all',
        to_emails: toList,
        cc_emails: cc.split(/[,\s]+/).map(e => e.trim()).filter(Boolean),
        message: msg,
      });
      toast.success('Schedule created');
      setTo(''); setCc(''); setMsg('');
      loadSchedules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create schedule');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/reports/activity/scheduled/${id}`);
    setSchedules(s => s.filter(x => x.id !== id));
    toast.success('Schedule removed');
  };

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Calendar className="h-5 w-5 text-indigo-600" />Schedule Report</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">

          {/* Existing schedules */}
          {schedules.length > 0 && (
            <div className="space-y-2">
              <Label className="text-slate-500 text-xs uppercase tracking-wide">Active Schedules</Label>
              {schedules.map(s => (
                <div key={s.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg text-xs">
                  <div>
                    <p className="font-medium text-slate-800">
                      {s.frequency === 'weekly' ? `Every ${days[s.day_of_week]}` : 'Daily'} at {String(s.hour).padStart(2,'0')}:00
                    </p>
                    <p className="text-slate-500">{PERIODS.find(p => p.value === s.period)?.label} · {s.view_type} · {s.to_emails?.join(', ')}</p>
                  </div>
                  <button onClick={() => remove(s.id)} className="text-red-400 hover:text-red-600 p-1">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <hr />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Frequency</Label>
              <Select value={freq} onValueChange={setFreq}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {freq === 'weekly' && (
              <div className="space-y-1.5">
                <Label>Day of Week</Label>
                <Select value={String(dow)} onValueChange={v => setDow(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {days.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Send at (hour)</Label>
              <Select value={String(hour)} onValueChange={v => setHour(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({length: 24}, (_,i) => (
                    <SelectItem key={i} value={String(i)}>
                      {String(i).padStart(2,'0')}:00 {i < 12 ? 'AM' : 'PM'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Report Period</Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>View Type</Label>
            <div className="flex gap-2">
              {['summary', 'detailed'].map(v => (
                <button key={v} onClick={() => setView(v)}
                  className={`px-3 py-1.5 rounded-md text-sm border transition-colors capitalize ${view === v ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  {v}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Users</Label>
            <UserSelector users={allUsers} selected={selectedUsers} onChange={setSelectedUsers} />
          </div>

          <div className="space-y-1.5">
            <Label>Send To <span className="text-red-500">*</span></Label>
            <Input value={to} onChange={e => setTo(e.target.value)} placeholder="email1, email2..." />
          </div>
          <div className="space-y-1.5">
            <Label>CC</Label>
            <Input value={cc} onChange={e => setCc(e.target.value)} placeholder="Optional" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea value={msg} onChange={e => setMsg(e.target.value)} placeholder="Optional note..." rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            Create Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main Page ───────────────────────────────────────────────────────────────
export default function ActivityReportPage() {
  const [period, setPeriod] = useState('this_week');
  const [viewType, setViewType] = useState('summary');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const tableRef = useRef(null);

  // Load available users once
  useEffect(() => {
    axios.get(`${API}/reports/activity/users`).then(r => setAllUsers(r.data)).catch(() => {});
  }, []);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        period,
        view_type: viewType,
        user_ids: selectedUsers.length ? selectedUsers.join(',') : 'all',
      };
      const res = await axios.get(`${API}/reports/activity`, { params });
      setData(res.data);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [period, viewType, selectedUsers]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Export helpers
  const periodLabel = PERIODS.find(p => p.value === period)?.label || period;
  const filename = `activity-report-${period}-${format(new Date(), 'yyyy-MM-dd')}`;

  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFontSize(16);
    doc.text(`CRM Activity Report — ${periodLabel}`, 14, 16);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${format(new Date(), 'MMM d, yyyy h:mm aa')}`, 14, 23);

    if (viewType === 'summary' && data?.users) {
      const rows = [];
      data.users.forEach(u => {
        rows.push([u.user_name, u.totals.calls, u.totals.emails, u.totals.sms, u.totals.calls + u.totals.emails + u.totals.sms]);
        Object.keys(u.by_date).sort().forEach(date => {
          const d = u.by_date[date];
          if (d.calls + d.emails + d.sms > 0)
            rows.push([`  ${format(parseISO(date), 'EEE MMM d')}`, d.calls || 0, d.emails || 0, d.sms || 0, d.calls + d.emails + d.sms]);
        });
      });
      autoTable(doc, {
        startY: 28,
        head: [['User / Date', 'Calls', 'Emails', 'SMS', 'Total']],
        body: rows,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [79, 70, 229] },
      });
    } else if (viewType === 'detailed' && data?.rows) {
      autoTable(doc, {
        startY: 28,
        head: [['Date', 'Time', 'User', 'Type', 'Recipient', 'Subject / Outcome']],
        body: data.rows.map(r => [
          r.date, r.time, r.user_name,
          r.type.toUpperCase(), r.recipient,
          r.subject || r.outcome || '',
        ]),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [79, 70, 229] },
      });
    }
    doc.save(`${filename}.pdf`);
  };

  const exportCSV = () => {
    let rows = [];
    if (viewType === 'summary' && data?.users) {
      rows.push(['User', 'Date', 'Calls', 'Emails', 'SMS', 'Total']);
      data.users.forEach(u => {
        rows.push([u.user_name, 'TOTAL', u.totals.calls, u.totals.emails, u.totals.sms, u.totals.calls + u.totals.emails + u.totals.sms]);
        Object.keys(u.by_date).sort().forEach(date => {
          const d = u.by_date[date];
          rows.push([u.user_name, date, d.calls, d.emails, d.sms, d.calls + d.emails + d.sms]);
        });
      });
    } else if (viewType === 'detailed' && data?.rows) {
      rows.push(['Date', 'Time', 'User', 'Type', 'Direction', 'Recipient', 'Subject', 'Outcome']);
      data.rows.forEach(r => rows.push([
        r.date, r.time, r.user_name, r.type, r.direction, r.recipient, r.subject, r.outcome,
      ]));
    }
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Activity Report');
    XLSX.writeFile(wb, `${filename}.xlsx`);
  };

  // Build HTML table for emailing
  const buildHtmlTable = () => {
    const th = 'padding:8px 12px;text-align:left;background:#4f46e5;color:#fff;font-size:13px';
    const td = 'padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:12px';
    const tdr = 'padding:8px 12px;border-bottom:1px solid #e2e8f0;font-size:12px;text-align:right';
    if (viewType === 'summary' && data?.users) {
      const bodyRows = data.users.map(u => `
        <tr style="background:#f8fafc"><td style="${td}"><strong>${u.user_name}</strong></td>
        <td style="${tdr}">${u.totals.calls}</td><td style="${tdr}">${u.totals.emails}</td>
        <td style="${tdr}">${u.totals.sms}</td><td style="${tdr}"><strong>${u.totals.calls + u.totals.emails + u.totals.sms}</strong></td></tr>
        ${Object.keys(u.by_date).sort().map(date => {
          const d = u.by_date[date];
          if (!d.calls && !d.emails && !d.sms) return '';
          return `<tr><td style="${td};padding-left:24px;color:#64748b">${format(parseISO(date), 'EEE, MMM d')}</td>
            <td style="${tdr};color:#3b82f6">${d.calls || '—'}</td><td style="${tdr};color:#8b5cf6">${d.emails || '—'}</td>
            <td style="${tdr};color:#10b981">${d.sms || '—'}</td><td style="${tdr}">${d.calls + d.emails + d.sms}</td></tr>`;
        }).join('')}
      `).join('');
      return `<table style="border-collapse:collapse;width:100%">
        <thead><tr><th style="${th}">User</th><th style="${th};text-align:right">Calls</th>
        <th style="${th};text-align:right">Emails</th><th style="${th};text-align:right">SMS</th>
        <th style="${th};text-align:right">Total</th></tr></thead><tbody>${bodyRows}</tbody></table>`;
    } else if (viewType === 'detailed' && data?.rows) {
      const bodyRows = data.rows.map(r => `
        <tr><td style="${td}">${r.date} ${r.time}</td><td style="${td}">${r.user_name}</td>
        <td style="${td}">${r.type.toUpperCase()}</td><td style="${td}">${r.recipient}</td>
        <td style="${td}">${r.subject || r.outcome || ''}</td></tr>
      `).join('');
      return `<table style="border-collapse:collapse;width:100%">
        <thead><tr><th style="${th}">Date/Time</th><th style="${th}">User</th>
        <th style="${th}">Type</th><th style="${th}">Recipient</th><th style="${th}">Subject/Outcome</th></tr></thead>
        <tbody>${bodyRows}</tbody></table>`;
    }
    return '';
  };

  // Summary totals bar
  const totals = data?.users
    ? data.users.reduce((acc, u) => ({
        calls: acc.calls + u.totals.calls,
        emails: acc.emails + u.totals.emails,
        sms: acc.sms + u.totals.sms,
      }), { calls: 0, emails: 0, sms: 0 })
    : null;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Activity Report
          </h1>
          <p className="text-slate-500 text-sm mt-1">CRM communications — calls, emails, and SMS by user</p>
        </div>

        {/* Export toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={exportPDF} disabled={!data} className="gap-1.5">
            <FileText className="h-4 w-4 text-red-500" /> PDF
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} disabled={!data} className="gap-1.5">
            <FileSpreadsheet className="h-4 w-4 text-green-600" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => setEmailOpen(true)} disabled={!data} className="gap-1.5">
            <Mail className="h-4 w-4 text-indigo-600" /> Email
          </Button>
          <Button variant="outline" size="sm" onClick={() => setScheduleOpen(true)} className="gap-1.5">
            <Calendar className="h-4 w-4 text-amber-600" /> Schedule
          </Button>
          <Button variant="outline" size="sm" onClick={fetchReport} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex flex-wrap gap-4 items-start">
            {/* Period */}
            <div className="space-y-1.5 min-w-[160px]">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Period</Label>
              <div className="flex flex-wrap gap-1.5">
                {PERIODS.map(p => (
                  <button key={p.value} onClick={() => setPeriod(p.value)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${period === p.value ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* View type */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">Report Type</Label>
              <div className="flex gap-1.5">
                {[['summary', 'Summary'], ['detailed', 'Detailed']].map(([v, l]) => (
                  <button key={v} onClick={() => setViewType(v)}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${viewType === v ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* User selector */}
            <div className="space-y-1.5 min-w-[220px] max-w-[320px] flex-1">
              <Label className="text-xs text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" />
                Users {selectedUsers.length > 0 && <Badge className="bg-indigo-100 text-indigo-700 text-[10px] px-1.5 py-0">{selectedUsers.length} selected</Badge>}
              </Label>
              <UserSelector users={allUsers} selected={selectedUsers} onChange={setSelectedUsers} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary totals strip */}
      {totals && viewType === 'summary' && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Total Calls',  value: totals.calls,  icon: Phone,          color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-100' },
            { label: 'Total Emails', value: totals.emails, icon: Mail,           color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-100' },
            { label: 'Total SMS',    value: totals.sms,    icon: MessageSquare,  color: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-100' },
          ].map(({ label, value, icon: Icon, color, bg, border }) => (
            <div key={label} className={`rounded-xl border ${border} ${bg} p-4 flex items-center gap-3`}>
              <Icon className={`h-6 w-6 ${color} shrink-0`} />
              <div>
                <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
                <p className="text-xs text-slate-500">{label}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            {viewType === 'summary' ? 'Summary by User' : 'Detailed Activity Log'}
          </CardTitle>
          {data && (
            <span className="text-xs text-slate-400">
              {data.start_date} → {data.end_date}
            </span>
          )}
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading report...
            </div>
          ) : !data ? null : viewType === 'summary' ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" ref={tableRef}>
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">User</th>
                    <th className="px-4 py-3 text-center font-semibold text-blue-600">
                      <span className="flex items-center justify-center gap-1"><Phone className="h-3.5 w-3.5" />Calls</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-purple-600">
                      <span className="flex items-center justify-center gap-1"><Mail className="h-3.5 w-3.5" />Emails</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-green-600">
                      <span className="flex items-center justify-center gap-1"><MessageSquare className="h-3.5 w-3.5" />SMS</span>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-slate-700">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.users?.length === 0 ? (
                    <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No activity found for this period</td></tr>
                  ) : (
                    data.users?.map(u => <SummaryUserRow key={u.user_id} userData={u} />)
                  )}
                </tbody>
                {data.users?.length > 0 && (
                  <tfoot>
                    <tr className="border-t bg-slate-100 font-bold">
                      <td className="px-4 py-3">Grand Total</td>
                      <td className="px-4 py-3 text-center text-blue-700">{totals?.calls}</td>
                      <td className="px-4 py-3 text-center text-purple-700">{totals?.emails}</td>
                      <td className="px-4 py-3 text-center text-green-700">{totals?.sms}</td>
                      <td className="px-4 py-3 text-center">{(totals?.calls || 0) + (totals?.emails || 0) + (totals?.sms || 0)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          ) : (
            /* Detailed table */
            <div className="overflow-x-auto">
              <table className="w-full text-sm" ref={tableRef}>
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Time</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">User</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Recipient</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Subject / Outcome</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-600">Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows?.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">No activity found for this period</td></tr>
                  ) : (
                    data.rows?.map((r, i) => (
                      <tr key={r.id || i} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">
                          {r.date ? format(parseISO(r.date), 'MMM d, yyyy') : '—'}
                        </td>
                        <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">
                          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{r.time}</span>
                        </td>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{r.user_name}</td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${TYPE_COLORS[r.type] || 'bg-slate-100 text-slate-600'}`}>
                            {TYPE_ICONS[r.type]}
                            {r.type.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-700 max-w-[200px] truncate">{r.recipient || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-600 max-w-[240px] truncate">{r.subject || r.outcome || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-500">
                          {r.duration_seconds ? `${Math.round(r.duration_seconds / 60)}m` : '—'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        periodLabel={periodLabel}
        tableHtml={buildHtmlTable()}
      />
      <ScheduleDialog
        open={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        allUsers={allUsers}
      />
    </div>
  );
}
